import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Eye,
  EyeOff,
  Sparkles,
  Brain,
  TrendingUp,
  Loader2,
} from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
import { motion } from 'framer-motion';
import { useAuthContext } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import logger from '../utils/logger';

/**
 * Login Page - World-Class UI Implementation
 *
 * Features:
 * - 2-column layout (form + visual content)
 * - SALONHUB logo integration
 * - Google login with icon
 * - shadcn/ui Design System
 * - Responsive design (mobile: 1-column, desktop: 2-column)
 * - WCAG 2.1 AA compliant
 * - Smooth micro-interactions
 *
 * @returns Login page component
 */
const Login: React.FC = () => {
  usePageTitle('ログイン');
  const navigate = useNavigate();
  const { login, googleLogin, loading, error, clearError } = useAuthContext();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  // Validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): string | null => {
    if (!email.trim()) {
      return 'メールアドレスを入力してください';
    }

    if (!validateEmail(email)) {
      return '有効なメールアドレスを入力してください';
    }

    if (!password.trim()) {
      return 'パスワードを入力してください';
    }

    if (password.length < 8) {
      return 'パスワードは8文字以上で入力してください';
    }

    return null;
  };

  // Handlers
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    clearError();
    setLocalError('');

    const validationError = validateForm();
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    try {
      await login({ email: email.trim(), password, rememberMe: false });
      logger.log('✅ ログイン成功:', email);
      navigate('/dashboard');
    } catch (err) {
      logger.error('❌ ログインエラー:', err);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      clearError();
      setLocalError('');
      await googleLogin();
      logger.log('✅ Google認証成功');
    } catch (err) {
      logger.error('❌ Google認証エラー:', err);
    }
  };

  const displayError = localError || error;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 min-h-screen">
      {/* LEFT COLUMN: Login Form */}
      <div className="flex flex-col justify-center p-4 md:p-8 bg-gray-50">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="max-w-[480px] mx-auto w-full">
            {/* SALONHUB Logo */}
            <img
              src="/assets/logos/salon-hub-logo.png"
              alt="SALON HUB"
              className="w-full max-w-[175px] h-auto mb-6 transition-transform duration-300 hover:scale-105"
            />

            {/* Title */}
            <h1 className="scroll-m-20 text-2xl font-bold tracking-tight text-gray-900 mb-6 leading-tight">
              ログイン
            </h1>

            {/* Login Form */}
            <form onSubmit={handleLogin}>
              {/* Email Field */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="email">メールアドレス</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white"
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2 mb-4">
                <Label htmlFor="password">パスワード</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (displayError) {
                        setLocalError('');
                        clearError();
                      }
                    }}
                    className="bg-white pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="パスワード表示切替"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
                {displayError && (
                  <p className="text-sm font-medium text-destructive">{displayError}</p>
                )}
              </div>

              {/* Login Button */}
              <Button
                type="submit"
                className="w-full min-h-[56px] py-2.5 mb-3"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'ログイン'
                )}
              </Button>

              {/* Divider */}
              <div className="relative mb-3">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-50 px-2 text-sm text-gray-500">
                  または
                </span>
              </div>

              {/* Google Login Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full min-h-[56px] py-2.5 bg-white"
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                <FcGoogle size={24} className="mr-2" />
                Googleでログイン
              </Button>

              {/* Links */}
              <div className="mt-4 text-center">
                <RouterLink
                  to="/password-reset"
                  className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  パスワードをお忘れの方
                </RouterLink>
              </div>

              <div className="mt-2 text-center">
                <p className="text-sm text-gray-600">
                  アカウントをお持ちでない方は{' '}
                  <RouterLink
                    to="/register"
                    className="font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    新規登録
                  </RouterLink>
                </p>
              </div>
            </form>
          </div>
        </motion.div>
      </div>

      {/* RIGHT COLUMN: Visual Content */}
      <div className="hidden md:flex flex-col justify-center items-center p-8 bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-white relative overflow-hidden">
        <div className="absolute top-[-50%] right-[-50%] w-[200%] h-[200%] bg-[radial-gradient(circle,rgba(255,255,255,0.1)_0%,transparent_70%)] animate-[pulse_8s_ease-in-out_infinite]" />

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          className="z-10 max-w-[500px] w-full"
        >
          {/* Welcome Message */}
          <h2 className="scroll-m-20 text-3xl md:text-4xl font-bold tracking-tight mb-2 text-center leading-[1.3] [text-shadow:0_2px_4px_rgba(0,0,0,0.1)]">
            顧客情報を、売上に変える。
          </h2>

          {/* Divider */}
          <Separator className="my-4 bg-white/30" />

          {/* Features */}
          <div className="mt-6">
            {/* Feature 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="mb-6">
                <h5 className="font-semibold text-xl mb-1 flex items-center gap-2">
                  <Sparkles className="w-7 h-7" />
                  予約データを自動分析
                </h5>
                <p className="text-base opacity-95 leading-relaxed">
                  散在する顧客情報を収益機会に変換
                </p>
              </div>
            </motion.div>

            {/* Feature 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="mb-6">
                <h5 className="font-semibold text-xl mb-1 flex items-center gap-2">
                  <Brain className="w-7 h-7" />
                  AIが売上機会を発見
                </h5>
                <p className="text-base opacity-95 leading-relaxed">
                  次回予約・メニュー最適化を自動提案
                </p>
              </div>
            </motion.div>

            {/* Feature 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div>
                <h5 className="font-semibold text-xl mb-1 flex items-center gap-2">
                  <TrendingUp className="w-7 h-7" />
                  結果にコミット
                </h5>
                <p className="text-base opacity-95 leading-relaxed">
                  リピート率20%向上・客単価10%向上
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
